import React, { useState, useEffect, useRef } from 'react';
import { Search, X, User, MessageCircle, Loader2, Sparkles } from 'lucide-react';
import { searchCustomerProfiles, getOrCreateC2cSession } from '../api/client';
import { isRTL } from '../utils/translations';

const CustomerSearchModal = ({ isOpen, onClose, onSelectSession }) => {
    const rtl = isRTL();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startingSessionId, setStartingSessionId] = useState(null);
    const debounceRef = useRef(null);

    const performSearch = async (searchTerm) => {
        if (!searchTerm || searchTerm.trim().length === 0) {
            setResults([]);
            return;
        }
        setLoading(true);
        try {
            const res = await searchCustomerProfiles(searchTerm.trim(), 0, 20);
            const content = res?.content || res?.data || (Array.isArray(res) ? res : []);
            setResults(Array.isArray(content) ? content : []);
        } catch (err) {
            console.error('Failed to search customers:', err);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            const handlePopState = () => {
                if (window.__luxvono_chat_level) return;
                onClose();
            };
            window.addEventListener('popstate', handlePopState);
            return () => window.removeEventListener('popstate', handlePopState);
        } else {
            setQuery('');
            setResults([]);
        }
    }, [isOpen, onClose]);

    const handleChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            performSearch(val);
        }, 300);
    };

    const handleStartChat = async (customer) => {
        setStartingSessionId(customer.id);
        try {
            const session = await getOrCreateC2cSession(customer.id);
            if (session?.id) {
                const displayName = customer.displayName || (customer.firstName ? `${customer.firstName} ${customer.lastName || ''}`.trim() : customer.username) || 'Customer';
                const avatar = customer.profileImageUrl || customer.profilePictureUrl || customer.avatarUrl || customer.avatar;

                onSelectSession({
                    sessionId: session.id,
                    recipientUserId: customer.id,
                    recipientName: displayName,
                    recipientAvatar: avatar,
                    recipientUsername: customer.username
                });
                onClose();
            }
        } catch (err) {
            console.error('Failed to start C2C session:', err);
            alert(rtl ? 'عذراً، تعذر بدء المحادثة حالياً. حاول مرة أخرى.' : 'Failed to start chat. Please try again.');
        } finally {
            setStartingSessionId(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="chat-modal-overlay"
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.78)',
                backdropFilter: 'blur(8px)',
                zIndex: 100060,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
            }}
            onClick={onClose}
        >
            <div
                className="chat-modal-card"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%',
                    maxWidth: '460px',
                    maxHeight: '80vh',
                    background: '#0B141A',
                    border: '1px solid rgba(255, 255, 255, 0.35)',
                    borderRadius: '20px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8), 0 0 30px rgba(255, 255, 255, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    direction: rtl ? 'rtl' : 'ltr'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(180deg, #16222b 0%, #0B141A 100%)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            background: 'rgba(255, 255, 255, 0.15)',
                            border: '1px solid rgba(255, 255, 255, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <MessageCircle size={20} color="#FFFFFF" />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, color: '#FFFFFF', fontSize: '15px', fontWeight: '800' }}>
                                {rtl ? 'محادثة جديدة مع عميل' : 'New Customer Chat'}
                            </h3>
                            <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                                {rtl ? 'ابحث بالاسم أو اسم المستخدم للتواصل المباشر' : 'Search by name or @username to chat'}
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'rgba(255, 255, 255, 0.06)',
                            border: 'none',
                            color: '#94A3B8',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Search Input */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
                    <div style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#1F2C34',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '0 12px'
                    }}>
                        <Search size={18} color="#FFFFFF" />
                        <input
                            type="text"
                            value={query}
                            onChange={handleChange}
                            placeholder={rtl ? 'ابحث بالاسم أو اسم المستخدم (@username)...' : 'Search by name or @username...'}
                            style={{
                                width: '100%',
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                color: '#FFFFFF',
                                fontSize: '13px',
                                padding: '12px 10px',
                                direction: rtl ? 'rtl' : 'ltr'
                            }}
                            autoFocus
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => { setQuery(''); setResults([]); }}
                                style={{ background: 'transparent', border: 'none', color: '#94A3B8', cursor: 'pointer', padding: 0 }}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Results List */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', minHeight: '220px', maxHeight: '50vh' }}>
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: '10px' }}>
                            <Loader2 size={28} color="#FFFFFF" className="animate-spin" />
                            <span style={{ fontSize: '12px', color: '#94A3B8' }}>{rtl ? 'جاري البحث في قاعدة العملاء...' : 'Searching customer directory...'}</span>
                        </div>
                    ) : results.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '36px 0', color: '#94A3B8' }}>
                            <User size={38} color="#334155" style={{ margin: '0 auto 10px auto' }} />
                            <p style={{ margin: 0, fontSize: '13px', color: '#CBD5E1' }}>
                                {query.trim()
                                    ? (rtl ? 'لم يتم العثور على عملاء بهذا الاسم' : 'No customers found with this name')
                                    : (rtl ? 'اكتب اسم العميل أو المعرف للبدء بالبحث' : 'Type name or username to start searching')}
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {results.map((c) => {
                                const displayName = (c.firstName ? `${c.firstName} ${c.lastName || ''}`.trim() : c.username) || c.displayName || 'Customer';
                                const avatar = c.profilePictureUrl || c.avatarUrl || c.avatar;
                                const isStarting = startingSessionId === c.id;

                                return (
                                    <div
                                        key={c.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px 12px',
                                            background: '#141E26',
                                            border: '1px solid rgba(255, 255, 255, 0.08)',
                                            borderRadius: '12px',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleStartChat(c)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                            e.currentTarget.style.background = '#1A2732';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                                            e.currentTarget.style.background = '#141E26';
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{
                                                width: '42px',
                                                height: '42px',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%)',
                                                border: '1px solid rgba(255, 255, 255, 0.4)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                                color: '#FFFFFF',
                                                fontWeight: 'bold',
                                                fontSize: '15px'
                                            }}>
                                                {avatar ? (
                                                    <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    displayName.charAt(0).toUpperCase()
                                                )}
                                            </div>
                                            <div>
                                                <h4 style={{ margin: 0, color: '#FFFFFF', fontSize: '13.5px', fontWeight: '700' }}>
                                                    {displayName}
                                                </h4>
                                                {c.username && (
                                                    <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                                                        @{c.username}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            disabled={isStarting}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleStartChat(c);
                                            }}
                                            style={{
                                                background: '#FFFFFF',
                                                border: 'none',
                                                color: '#000000',
                                                borderRadius: '8px',
                                                padding: '6px 12px',
                                                fontSize: '11.5px',
                                                fontWeight: '800',
                                                cursor: isStarting ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                boxShadow: '0 2px 8px rgba(255, 255, 255, 0.3)'
                                            }}
                                        >
                                            {isStarting ? (
                                                <Loader2 size={13} className="animate-spin" />
                                            ) : (
                                                <MessageCircle size={13} />
                                            )}
                                            <span>{rtl ? 'بدء محادثة' : 'Chat'}</span>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomerSearchModal;
